/*
  Timeline, wave 3 (screen 10):
  - PlanCard          #3 Auto-plan: Start · Work week · Buffer → the proposed handover; links to Execution dates
  - TimelineGantt     #2 a real Gantt on the foundation <Gantt />: months axis, red today line, planned handover
                      (dashed, with the overdue band), the proposed / forecast handover; #4 a milestones lane
                      (invoices, VO approvals, retention); delivery stages; execution tasks (grouped like the
                      wave 2 view); hatched proposals; #8 dependency arrows with the critical path in red and the
                      slip of late items pushed to what follows
  - ApplyPlanDialog   #3 what Apply writes (stage planned dates, task dates + their sub-items, optionally the
                      handover date) before it writes it
  - openLookahead()   #7 the next three weeks as a print-ready page (Save as PDF) for the consultant / contractor
*/
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, Flag, Loader2, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gantt, GanttLegend } from '@/components/ui/gantt';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { StatusPill } from '@/components/ui/status-pill';
import { notify } from '@/components/ui/notify';
import { formatDate, formatNumber } from '@/lib/format';
import { PX_PER_DAY, formatDayRange, toDay, dayToIso, workingDaysBetween } from '@/lib/gantt';
import { cn } from '@/lib/utils';
import { Panel } from './ProjectUi';
import { formatExecutionTaskTitle } from './executionModel';
import { groupTimelineItems, timelineItemsFromTasks } from './timelineModel';
import {
  PLAN_BUFFERS, PLAN_WORK_WEEKS, billingMilestones, criticalPath, forecastSlips, isOngoingStage, isStageDone,
  isTaskClosed, lookaheadHtml, lookaheadRows, taskDates,
} from './planModel';

const text = (value) => String(value ?? '').trim();
const shortDay = (iso) => (iso ? formatDate(iso, { year: 'never' }) : '');
const shortRange = (start, end) => formatDayRange(toDay(start), toDay(end)).replace(/ \d{4}$/, '');
const GROUP_ROW_LIMIT = 30;
const isoToLocalDate = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
};
const localDateToIso = (date) => (date
  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  : '');

/** The plan's start: "Fri, 2 Oct 2026" opening a calendar (the app date format, not the browser's). */
function StartPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-9 min-w-[158px] justify-start font-medium" disabled={disabled} aria-label={`Plan start: ${formatDate(value, { weekday: true })}`} data-testid="timeline-plan-start">
          <CalendarDays aria-hidden="true" />
          {formatDate(value, { weekday: true })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={isoToLocalDate(value)}
          defaultMonth={isoToLocalDate(value)}
          onSelect={(date) => {
            if (!date) return;
            onChange(localDateToIso(date));
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ plan card (#3) */

const items = (count, one = 'item', many = 'items') => `${formatNumber(count, { decimals: 0 })} open ${count === 1 ? one : many}`;

/** The plan card's heading and sentence, from the Auto-plan result. Pure (unit-tested). */
export function planCardText({ plan, hasDatedItems, appliedPlan }) {
  const proposals = plan?.proposedCount || 0;
  const fresh = plan?.newCount ?? proposals;
  const replanned = plan?.replannedCount || 0;
  const applied = appliedPlan?.applied_at
    ? `Plan applied ${formatDate(appliedPlan.applied_at)}${appliedPlan.applied_by ? ` by ${appliedPlan.applied_by}` : ''}. `
    : '';
  if (plan?.replan && proposals) {
    return {
      title: `Re-plan: ${items(proposals)} get new dates.`,
      body: `Every open item is planned again from ${formatDate(plan.start)} with template durations — the dates it has now are ignored — or `,
    };
  }
  if (replanned) {
    return {
      title: fresh
        ? `${items(fresh, 'item has', 'items have')} no dates yet · ${formatNumber(replanned, { decimals: 0 })} slipped.`
        : `${items(replanned, 'item', 'items')} slipped — re-plan ${replanned === 1 ? 'it' : 'them'}.`,
      body: `${applied}Auto-plan moves what is late, and what waits for it, to start from ${formatDate(plan.start)} keeping its length${fresh ? `, and plans the ${formatNumber(fresh, { decimals: 0 })} without dates` : ''}; Apply plan writes the new dates. Or `,
    };
  }
  if (!hasDatedItems) return { title: 'No dates on any task yet.', body: proposals ? `Auto-plan the ${items(proposals)} from template durations (Variations stays ongoing), or ` : 'Change single dates in Execution, or ' };
  if (proposals) {
    return {
      title: `${items(proposals, 'item has', 'items have')} no dates yet.`,
      body: `Auto-plan the ${items(proposals)} from template durations (Variations stays ongoing), or `,
    };
  }
  return { title: 'Every open item has dates.', body: `${applied}Change single dates in Execution, or ` };
}

export function PlanCard({
  plan, settings, onSettingsChange, hasDatedItems, canPlan, canSetDates, onSetDates, appliedPlan, closed = '',
}) {
  // `closed`: the project's status when it is Completed / Cancelled — nothing is planned any more
  if (closed) {
    return (
      <Panel className="flex items-center gap-4 px-5 py-4" data-testid="timeline-plan">
        <span className="ui-icon-tile tone-gray h-11 w-11" aria-hidden="true"><WandSparkles className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1" data-testid="timeline-plan-text">
          <h3 className="text-[15px] font-bold text-ink">{closed} project — nothing to plan.</h3>
          <p className="mt-1 text-[13px] text-ink-3">Auto-plan is off for Completed and Cancelled projects; the Gantt shows the dates as they were.</p>
        </div>
      </Panel>
    );
  }
  const proposals = plan?.proposedCount || 0;
  const { title, body } = planCardText({ plan, hasDatedItems, appliedPlan });
  // "Re-plan all open items" — offered when the page passes the switch's value to buildAutoPlan (replan)
  const offerReplan = Boolean(plan?.replanAvailable);
  const week = PLAN_WORK_WEEKS.find((entry) => entry.key === settings.workWeek) || PLAN_WORK_WEEKS[1];
  return (
    <Panel
      className={cn('flex flex-wrap items-center gap-x-5 gap-y-4 px-5 py-4', proposals ? 'border-[color:var(--lime-300,#b9e36b)] shadow-[0_0_0_3px_var(--lime-50,#f4fbe6)]' : '')}
      data-testid="timeline-plan"
    >
      <span className="ui-icon-tile tone-lime h-11 w-11" aria-hidden="true"><WandSparkles className="h-5 w-5" /></span>
      <div className="min-w-[240px] flex-1" data-testid={hasDatedItems ? 'timeline-plan-text' : 'timeline-empty'}>
        <h3 className="text-[15px] font-bold text-ink">{title}</h3>
        <p className="mt-1 max-w-[560px] text-[13px] leading-snug text-ink-3">
          {body}
          <button
            type="button"
            onClick={onSetDates}
            disabled={!canSetDates}
            className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline disabled:pointer-events-none disabled:opacity-50"
            data-testid="timeline-set-dates"
          >
            set dates in Execution
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </p>
        {offerReplan ? (
          <label className="mt-2 inline-flex items-center gap-2 text-[12.5px] font-medium text-ink-2">
            <Switch
              checked={Boolean(settings.replan)}
              onCheckedChange={(replan) => onSettingsChange({ replan })}
              disabled={!canPlan}
              aria-label="Re-plan all open items"
              data-testid="timeline-plan-replan"
            />
            Re-plan all open items ({formatNumber(plan.savedOpenCount, { decimals: 0 })} with dates)
          </label>
        ) : null}
      </div>
      {proposals ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block space-y-1">
              <span className="block text-[11.5px] font-semibold text-ink-3">Start</span>
              <StartPicker value={settings.start} onChange={(start) => onSettingsChange({ start })} disabled={!canPlan} />
            </label>
            <label className="block space-y-1">
              <span className="block text-[11.5px] font-semibold text-ink-3">Work week</span>
              <Select value={settings.workWeek} onValueChange={(workWeek) => onSettingsChange({ workWeek })} disabled={!canPlan}>
                <SelectTrigger className="h-9 w-[118px]" aria-label="Work week" data-testid="timeline-plan-week"><SelectValue>{week.label}</SelectValue></SelectTrigger>
                <SelectContent>
                  {PLAN_WORK_WEEKS.map((entry) => <SelectItem key={entry.key} value={entry.key}>{entry.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block space-y-1">
              <span className="block text-[11.5px] font-semibold text-ink-3">Buffer</span>
              <Select value={String(settings.buffer)} onValueChange={(value) => onSettingsChange({ buffer: Number(value) })} disabled={!canPlan}>
                <SelectTrigger className="h-9 w-[86px]" aria-label="Buffer" data-testid="timeline-plan-buffer"><SelectValue>{settings.buffer}%</SelectValue></SelectTrigger>
                <SelectContent>
                  {PLAN_BUFFERS.map((value) => <SelectItem key={value} value={String(value)}>{value}%</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>
          <div className="min-w-[170px] border-l border-line pl-4" data-testid="timeline-plan-handover">
            <p className="text-[11.5px] font-semibold text-ink-3">Proposed handover</p>
            <p className="mt-0.5 flex items-center gap-1.5 font-heading text-[16px] font-extrabold text-ink">
              <span className="inline-block h-2.5 w-2.5 rotate-45 rounded-[2px] bg-brand" aria-hidden="true" />
              {formatDate(plan.handover)}
            </p>
            <p className="mt-0.5 text-[11.5px] text-ink-3">
              {plan.workingDays} working days{settings.buffer ? ` · incl. ${settings.buffer}% buffer` : ''}
            </p>
          </div>
        </>
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ apply dialog (#3) */

export function ApplyPlanDialog({ open, onOpenChange, plan, endDate, canMoveEndDate, onApply }) {
  const [moveEnd, setMoveEnd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (open) {
      setMoveEnd(false);
      setError('');
    }
  }, [open]);
  if (!plan) return null;
  const proposed = plan.items.filter((item) => item.proposed);
  const stages = proposed.filter((item) => item.kind === 'stage');
  const tasks = proposed.filter((item) => item.kind === 'execution');
  const moved = proposed.filter((item) => item.replanned && item.was);
  const range = (start, end) => formatDayRange(toDay(start), toDay(end));
  const differs = plan.handover && plan.handover !== text(endDate).slice(0, 10);
  const apply = async () => {
    setSaving(true);
    setError('');
    try {
      await onApply({ updateEndDate: Boolean(moveEnd && differs && canMoveEndDate) });
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || 'Could not apply the plan');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="timeline-apply-dialog">
        <DialogHeader>
          <DialogTitle>Apply the plan?</DialogTitle>
          <DialogDescription>The proposed dates are written to the project. One Undo puts everything back.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-[13px] text-ink-2">
          {stages.length ? <li>• <b className="font-semibold text-ink">{stages.length} delivery stage{stages.length === 1 ? '' : 's'}</b> {stages.length === 1 ? 'gets' : 'get'} planned dates ({shortDay(stages[0].start)} – {shortDay(stages[stages.length - 1].end)})</li> : null}
          {tasks.length ? <li>• <b className="font-semibold text-ink">{tasks.length} execution task{tasks.length === 1 ? '' : 's'}</b> {tasks.length === 1 ? 'gets' : 'get'} start and due dates; their sub-items without dates follow</li> : null}
          {moved.length ? (
            <li data-testid="timeline-apply-moved">
              • <b className="font-semibold text-ink">{moved.length} already dated</b> {moved.length === 1 ? 'moves' : 'move'} (re-planned):{' '}
              {moved.slice(0, 3).map((item, index) => (
                <span key={item.key}>
                  {index ? '; ' : ''}{item.label} {range(item.was.start, item.was.end)} → {range(item.start, item.end)}
                </span>
              ))}
              {moved.length > 3 ? ` and ${moved.length - 3} more` : ''}
            </li>
          ) : null}
          <li>• Proposed handover <b className="font-semibold text-ink">{formatDate(plan.handover)}</b></li>
        </ul>
        {differs && canMoveEndDate ? (
          <label className="mt-1 flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2">
            <Checkbox checked={moveEnd} onCheckedChange={(value) => setMoveEnd(Boolean(value))} aria-label="Also move the handover date" data-testid="timeline-apply-move-end" className="mt-0.5" />
            <span>
              Also move the handover date from <b className="font-semibold text-ink">{endDate ? formatDate(endDate) : 'not set'}</b> to <b className="font-semibold text-ink">{formatDate(plan.handover)}</b>
              <span className="block text-[12px] text-ink-3">Leave it off to keep the contract date and show the plan as a recovery plan.</span>
            </span>
          </label>
        ) : null}
        {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid="timeline-apply-error">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={apply} disabled={saving} data-testid="timeline-apply-confirm">
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <WandSparkles aria-hidden="true" />}
            Apply plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ the Gantt (#2, #4, #8) */

const stageMarker = (stage, index) => String(stage.order || index + 1).padStart(2, '0');
const stageDot = (stage) => {
  if (isStageDone(stage)) return 'navy';
  const status = text(stage.status);
  if (/progress|submitted/i.test(status)) return 'blue';
  if (/reject|bottleneck/i.test(status)) return 'red';
  return 'gray';
};
const approvedOn = (stage) => {
  const history = Array.isArray(stage.status_history) ? stage.status_history : [];
  const entry = [...history].reverse().find((item) => /approved|completed/i.test(text(item?.status)));
  return text(entry?.at || entry?.date || (isStageDone(stage) ? stage.status_since : '')).slice(0, 10);
};

/**
 * A row note on a surface chip: the dashed handover / proposal lines pass behind it instead of through the words
 * (10 #2). `status`: a navy pill in front ("Approved  no dates recorded").
 */
function RowNote({ status, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-surface px-1.5 py-0.5">
      {status ? <StatusPill tone="navy" label={status} size="sm" /> : null}
      <span className="text-ink-3">{children}</span>
    </span>
  );
}

/** "critical" under the row's name (not beside it), so the name keeps the label column (10 #0). */
function CriticalTag() {
  return (
    <span className="inline-flex items-center gap-1 font-bold uppercase tracking-[0.04em] text-status-red-ink" title="On the critical path: a day late here moves the end date">
      <Flag className="h-3 w-3" aria-hidden="true" />
      critical
    </span>
  );
}

const withCritical = (isCritical, sublabel) => {
  if (!isCritical) return sublabel || undefined;
  return sublabel ? <><CriticalTag /> · {sublabel}</> : <CriticalTag />;
};

/**
 * Everything the Gantt shows, from the project's data and the plan. Pure (memoised by the component).
 * Returns { sections, markers, dependencies, range, timelineItems (for the lookahead), counts }.
 */
export function buildTimelineModel({
  project = {}, stages = [], tasks = [], tree, invoices = [], variations = [], plan, showProposal = true, group = 'task', today, weekend = [0],
  compact = false,
}) {
  const projectClosed = /completed|cancelled|closed/i.test(text(project.status));
  // a completed / cancelled project is not planned any more: no proposals on its Gantt
  const proposalByKey = new Map((showProposal && plan && !projectClosed ? plan.items.filter((item) => item.proposed) : []).map((item) => [item.key, item]));
  const mainTasks = tasks.filter((task) => !task.parent_id);
  const handoverPlanned = text(project.end_date).slice(0, 10);
  const proposedHandover = proposalByKey.size ? plan.handover : null;

  // Saved schedule (stage planned dates + dated open tasks) → forecast of slips (#8)
  const savedItems = [];
  const closedKeys = new Set();
  stages.forEach((stage) => {
    if (isOngoingStage(stage) || !(stage.planned_start && stage.planned_end)) return;
    const key = `stage:${stage.id}`;
    savedItems.push({ key, start: stage.planned_start, end: stage.planned_end });
    if (isStageDone(stage)) closedKeys.add(key);
  });
  mainTasks.forEach((task) => {
    const dates = taskDates(task, tree);
    if (!dates) return;
    const key = `task:${task.id}`;
    savedItems.push({ key, start: dates.start, end: dates.end });
    if (isTaskClosed(task)) closedKeys.add(key);
  });
  const allEdges = plan?.edges || [];
  const savedKeys = new Set(savedItems.map((item) => item.key));
  const forecast = forecastSlips(savedItems, allEdges.filter((edge) => savedKeys.has(edge.from) && savedKeys.has(edge.to)), { weekend, today, closed: closedKeys });

  // Critical path over what is drawn (saved + proposed; a re-planned item counts with its proposal)
  const drawn = [
    ...savedItems.filter((item) => !proposalByKey.has(item.key)).map((item) => ({ ...item, ...(forecast.moved.get(item.key) || {}) })),
    ...[...proposalByKey.values()],
  ].filter((item) => !closedKeys.has(item.key));
  const drawnKeys = new Set(drawn.map((item) => item.key));
  const drawnEdges = allEdges.filter((edge) => drawnKeys.has(edge.from) && drawnKeys.has(edge.to));
  const critical = drawn.length ? criticalPath(drawn, drawnEdges, weekend) : { items: new Set(), edges: new Set() };

  const barsFor = (key, saved, { closed = false, label } = {}) => {
    const bars = [];
    const proposal = proposalByKey.get(key);
    const replanned = Boolean(saved && proposal);
    if (saved) {
      const late = !closed && saved.end < today;
      bars.push({
        // a re-planned item: its old dates stay visible; the proposal carries the bar id the arrows use
        id: replanned ? `was-${key}` : `bar-${key}`,
        start: saved.start,
        end: saved.end,
        kind: closed ? 'done' : late ? 'late' : 'plan',
        label: label || undefined,
        title: replanned ? `Planned before: ${formatDayRange(toDay(saved.start), toDay(saved.end))} — Auto-plan moves it` : undefined,
        testId: `timeline-${replanned ? 'was' : 'bar'}-${key.replace(':', '-')}`,
      });
      if (replanned) {
        // the slip stays readable (#8): "+12 d · re-plan 2–8 Oct" replaces the red "+12 d · forecast" extension
        const slip = Math.max(0, workingDaysBetween(saved.end, proposal.end, { weekend }) - 1);
        bars.push({
          id: `bar-${key}`,
          start: proposal.start,
          end: proposal.end,
          kind: 'proposed',
          label: `${slip ? `+${slip} d · ` : ''}re-plan ${shortRange(proposal.start, proposal.end)}`,
          title: `Re-plan proposal: ${formatDayRange(toDay(proposal.start), toDay(proposal.end))} (${proposal.replanned === 'pushed' ? 'what it waits for moved' : proposal.replanned === 'all' ? 're-plan all' : 'it slipped'})`,
          testId: `timeline-bar-${key.replace(':', '-')}`,
        });
        return bars;
      }
      const moved = forecast.moved.get(key);
      if (moved && moved.end > saved.end) {
        const from = dayToIso(toDay(saved.end) + 1);
        bars.push({
          id: `slip-${key}`,
          start: from < moved.start ? moved.start : from,
          end: moved.end,
          kind: 'late',
          label: `+${moved.slip || 1} d · forecast ${shortDay(moved.end)}`,
          title: `Forecast: ${formatDayRange(toDay(moved.start), toDay(moved.end))}`,
          testId: `timeline-slip-${key.replace(':', '-')}`,
        });
      }
    } else if (proposal) {
      bars.push({ id: `bar-${key}`, start: proposal.start, end: proposal.end, kind: 'proposed', testId: `timeline-bar-${key.replace(':', '-')}` });
    }
    return bars;
  };
  const isCritical = (key) => critical.items.has(key);

  // Milestones lane (#4)
  const billing = billingMilestones({ invoices, variations, handover: proposedHandover || handoverPlanned || null });

  // Delivery stages
  const approvedVos = variations.filter((variation) => variation.status === 'Approved');
  const stageRows = stages.map((stage, index) => {
    const key = `stage:${stage.id}`;
    const row = {
      id: `stage-${stage.id}`,
      marker: stageMarker(stage, index),
      label: stage.name,
      dot: stageDot(stage),
      testId: `timeline-stage-${stage.id}`,
    };
    if (isOngoingStage(stage)) {
      const dates = variations.map((variation) => text(variation.created_at).slice(0, 10)).filter(Boolean).sort();
      if (dates.length) {
        const last = [today, ...dates].sort().pop();
        row.bars = [{ id: `bar-${key}`, start: dates[0], end: last, kind: 'plan', tone: 'violet', label: `${approvedVos.length} approved · ongoing` }];
      } else {
        row.note = <RowNote>Ongoing — no variations yet</RowNote>;
        row.noteText = 'Ongoing — no variations yet';
      }
      // phones: the pill would leave no room for the name — it goes under it
      if (approvedVos.length && compact) row.sublabel = <span className="font-semibold text-status-violet-ink">{approvedVos.length} approved</span>;
      else row.labelAddon = approvedVos.length ? <StatusPill tone="violet" label={`${approvedVos.length} approved`} size="sm" /> : null;
      return row;
    }
    const saved = stage.planned_start && stage.planned_end ? { start: stage.planned_start, end: stage.planned_end } : null;
    row.bars = barsFor(key, saved, { closed: isStageDone(stage) });
    if (!row.bars.length) {
      const approved = isStageDone(stage) ? approvedOn(stage) : '';
      if (approved) row.milestones = [{ id: `approved-${stage.id}`, date: approved, label: `${text(stage.status)} ${shortDay(approved)}`, tone: 'navy', shape: 'dot' }];
      else if (isStageDone(stage)) {
        row.noteText = `${text(stage.status) || 'Approved'} · no dates recorded`;
        row.note = <RowNote status={text(stage.status) || 'Approved'}>no dates recorded</RowNote>;
      } else {
        row.noteText = 'No dates yet';
        row.note = <RowNote>No dates yet</RowNote>;
      }
    }
    row.sublabel = withCritical(isCritical(key), row.sublabel);
    return row;
  });
  const stagesDone = stages.filter(isStageDone).length;
  const stagesToPlan = [...proposalByKey.values()].filter((item) => item.kind === 'stage').length;

  // Execution rows
  const executionRows = [];
  const closedUndated = mainTasks.filter((task) => isTaskClosed(task) && !taskDates(task, tree));
  if (closedUndated.length) {
    const done = closedUndated.filter((task) => text(task.status) === 'Done').length;
    const notRequired = closedUndated.length - done;
    executionRows.push({
      id: 'exec-closed',
      label: [done ? `${done} task${done === 1 ? '' : 's'} done` : '', notRequired ? `${notRequired} not required` : ''].filter(Boolean).join(' · '),
      dot: 'navy',
      note: <RowNote>No dates recorded</RowNote>,
      noteText: 'No dates recorded',
      testId: 'timeline-closed-row',
      // 10 #2: collapsed; its chevron lists the closed main tasks (TimelineGantt opens it)
      children: closedUndated.map((task) => ({
        id: `closed-${task.id}`,
        label: text(task.title) || 'Task',
        indent: 1,
        dot: text(task.status) === 'Done' ? 'navy' : 'gray',
        note: <RowNote status={text(task.status) === 'Done' ? null : text(task.status)}>no dates recorded</RowNote>,
        noteText: 'no dates recorded',
        testId: `timeline-closed-${task.id}`,
      })),
    });
  }
  const dated = tasks.filter((task) => task.start_date || task.due_date);
  if (group === 'task') {
    mainTasks.forEach((task) => {
      if (isTaskClosed(task) && !taskDates(task, tree)) return;
      const key = `task:${task.id}`;
      const saved = taskDates(task, tree);
      const closed = isTaskClosed(task);
      const people = text(task.done_by);
      executionRows.push({
        id: `task-${task.id}`,
        label: text(task.title) || 'Task',
        sublabel: withCritical(isCritical(key), [text(task.category), people || (closed ? '' : 'Unassigned')].filter(Boolean).join(' · ')),
        dot: closed ? 'navy' : /progress/i.test(text(task.status)) ? 'blue' : /bottleneck/i.test(text(task.status)) ? 'red' : 'gray',
        bars: barsFor(key, saved, { closed }),
        note: saved || proposalByKey.has(key) ? undefined : <RowNote>No dates yet</RowNote>,
        noteText: saved || proposalByKey.has(key) ? undefined : 'No dates yet',
        testId: 'timeline-group-row',
      });
      // its sub-items with dates of their own (first 30)
      const children = [];
      const walk = (parentId) => (tree?.children?.get(parentId) || []).forEach((child) => {
        if (child.start_date || child.due_date) children.push(child);
        walk(child.id);
      });
      walk(task.id);
      children.slice(0, GROUP_ROW_LIMIT).forEach((child) => {
        const dates = taskDates(child, null);
        executionRows.push({
          id: `sub-${child.id}`,
          label: formatExecutionTaskTitle(child, tree?.byId) || child.title,
          indent: 1,
          dot: isTaskClosed(child) ? 'navy' : 'gray',
          bars: dates ? [{ id: `bar-sub-${child.id}`, start: dates.start, end: dates.end, kind: isTaskClosed(child) ? 'done' : dates.end < today ? 'late' : 'plan' }] : [],
          testId: `timeline-row-${child.id}`,
        });
      });
    });
  } else {
    // 10 #6: proposed (not yet dated) main tasks sit in their category / building group, hatched
    const fallback = group === 'building' ? 'Whole project' : 'No category';
    const taskById = new Map(mainTasks.map((task) => [task.id, task]));
    const bucketOf = (item) => {
      const task = taskById.get(item.id);
      return (group === 'building' ? text(task?.zone) : text(task?.category)) || fallback;
    };
    const proposedByBucket = new Map();
    [...proposalByKey.values()].filter((item) => item.kind === 'execution').forEach((item) => {
      const bucket = bucketOf(item);
      if (!proposedByBucket.has(bucket)) proposedByBucket.set(bucket, []);
      proposedByBucket.get(bucket).push(item);
    });
    const groups = groupTimelineItems(timelineItemsFromTasks(dated, tree), group);
    proposedByBucket.forEach((list, bucket) => {
      if (!groups.some((entry) => entry.key === bucket)) groups.push({ key: bucket, label: bucket, items: [], count: 0 });
    });
    groups.sort((a, b) => (a.key === fallback) - (b.key === fallback) || a.key.localeCompare(b.key, undefined, { numeric: true }));
    groups.forEach((entry) => {
      const proposed = proposedByBucket.get(entry.key) || [];
      const savedSpan = entry.start && entry.end
        ? { start: dayToIso(Math.round(entry.start.getTime() / 86400000)), end: dayToIso(Math.round(entry.end.getTime() / 86400000)) }
        : null;
      const proposedSpan = proposed.length
        ? { start: proposed.map((item) => item.start).sort()[0], end: proposed.map((item) => item.end).sort().pop() }
        : null;
      const count = entry.count + proposed.length;
      const groupBars = [];
      if (savedSpan) groupBars.push({ id: `bar-group-${entry.key}`, start: savedSpan.start, end: savedSpan.end, kind: 'plan' });
      if (proposedSpan) groupBars.push({ id: `bar-group-proposed-${entry.key}`, start: proposedSpan.start, end: proposedSpan.end, kind: 'proposed', label: savedSpan ? `${proposed.length} to plan` : undefined });
      executionRows.push({
        id: `group-${entry.key}`,
        label: entry.label,
        sublabel: [`${count} task${count === 1 ? '' : 's'}`, proposed.length ? `${proposed.length} to plan` : ''].filter(Boolean).join(' · '),
        bars: groupBars,
        testId: 'timeline-group-row',
      });
      proposed.forEach((item) => {
        executionRows.push({
          id: `task-${item.id}`,
          label: item.label,
          sublabel: withCritical(isCritical(item.key), item.replanned ? 'Re-plan' : 'To plan'),
          indent: 1,
          dot: 'gray',
          bars: barsFor(item.key, null),
          testId: `timeline-row-${item.id}`,
        });
      });
      entry.items.slice(0, GROUP_ROW_LIMIT).forEach((item) => {
        const a = dayToIso(Math.round(item.start.getTime() / 86400000));
        const b = dayToIso(Math.round(item.end.getTime() / 86400000));
        executionRows.push({
          id: `item-${item.id}`,
          label: item.title,
          indent: 1,
          dot: item.status === 'Done' ? 'navy' : 'gray',
          bars: [{ id: `bar-item-${item.id}`, start: a, end: b, kind: item.status === 'Done' ? 'done' : b < today ? 'late' : 'plan' }],
          testId: `timeline-row-${item.id}`,
        });
      });
    });
  }
  const tasksToPlan = [...proposalByKey.values()].filter((item) => item.kind === 'execution').length;

  // Markers
  const markers = [];
  if (handoverPlanned) {
    markers.push({
      key: 'handover',
      date: handoverPlanned,
      label: `Planned handover · ${shortDay(handoverPlanned)}`,
      tone: 'red',
      dashed: true,
      shadeTo: !projectClosed && handoverPlanned < today ? 'today' : undefined,
    });
  }
  if (proposedHandover) markers.push({ key: 'proposed', date: proposedHandover, label: `Proposed handover · ${shortDay(proposedHandover)}`, tone: 'navy', dashed: true });
  const forecastEnd = forecast.moved.size ? forecast.lastEnd : null;
  if (forecastEnd && forecastEnd !== proposedHandover) markers.push({ key: 'forecast', date: forecastEnd, label: `Forecast end · ${shortDay(forecastEnd)}`, tone: 'amber', dashed: true });

  // Visible range: every bar and marker (and today), not the old invoices — those count as "earlier"
  const barDates = [];
  [...stageRows, ...executionRows].forEach((row) => (row.bars || []).forEach((bar) => barDates.push(bar.start, bar.end)));
  [...stageRows].forEach((row) => (row.milestones || []).forEach((milestone) => barDates.push(milestone.date)));
  markers.forEach((marker) => barDates.push(marker.date));
  billing.milestones.forEach((milestone) => { if (milestone.date >= today) barDates.push(milestone.date); });
  barDates.push(today);
  const days = barDates.map(toDay).filter((day) => day !== null);
  // a little room before the first date and after the last one (its date label is written after the bar)
  const first = Math.min(...days) - 5;
  const contentEnd = Math.max(...days);
  const last = contentEnd + 16;
  // contentEnd: TimelineGantt adds room after it for the labels written after the last bar / marker (zoom-aware)
  const range = { start: dayToIso(Math.min(first, toDay(today) - 45)), end: dayToIso(Math.max(last, toDay(today) + 30)), contentEnd: dayToIso(contentEnd) };
  const earlier = billing.milestones.filter((milestone) => milestone.date < range.start);
  const shown = mergeSameDay(billing.milestones.filter((milestone) => milestone.date >= range.start));
  const milestoneRows = billingRows({ billing, shown, earlier, rangeStart: range.start });

  const sections = [
    {
      key: 'milestones',
      label: 'Milestones',
      meta: 'invoices, VO approvals, retention',
      rows: milestoneRows,
    },
    { key: 'stages', label: 'Delivery stages', meta: `${stagesDone} of ${stages.length} approved${stagesToPlan ? ` · ${stagesToPlan} to plan` : ''}`, rows: stageRows },
    { key: 'execution', label: 'Execution', meta: `${mainTasks.length} task${mainTasks.length === 1 ? '' : 's'}${tasksToPlan ? ` · ${tasksToPlan} to plan` : ''}`, rows: executionRows },
  ];

  const dependencies = drawnEdges.map((edge) => ({
    from: `bar-${edge.from}`,
    to: `bar-${edge.to}`,
    critical: critical.edges.has(`${edge.from}>${edge.to}`),
  }));

  // What the lookahead lists (#7): saved and proposed items with an owner
  const lookaheadItems = [];
  stages.forEach((stage) => {
    if (isOngoingStage(stage) || isStageDone(stage)) return;
    const key = `stage:${stage.id}`;
    const saved = stage.planned_start && stage.planned_end ? { start: stage.planned_start, end: stage.planned_end } : null;
    const proposal = proposalByKey.get(key);
    const dates = saved || proposal;
    if (!dates) return;
    lookaheadItems.push({ kind: 'stage', label: `${stageMarker(stage, 0)} ${stage.name}`, start: dates.start, end: dates.end, status: text(stage.status) || 'Not Started', owner: text(project.project_engineer), proposed: !saved, note: forecast.moved.get(key) ? `Forecast ${formatDate(forecast.moved.get(key).end)}` : '' });
  });
  mainTasks.forEach((task) => {
    if (isTaskClosed(task)) return;
    const key = `task:${task.id}`;
    const saved = taskDates(task, tree);
    const dates = saved || proposalByKey.get(key);
    if (!dates) return;
    lookaheadItems.push({ kind: 'execution', label: text(task.title), start: dates.start, end: dates.end, status: text(task.status) || 'Not Started', owner: text(task.done_by) || text(project.project_engineer), proposed: !saved, note: text(task.category) });
  });
  billing.milestones.forEach((milestone) => {
    if (milestone.kind === 'variation') return;
    lookaheadItems.push({ kind: 'milestone', label: milestone.kind === 'retention' ? milestone.label : `Invoice ${milestone.label}`, start: milestone.date, end: milestone.date, status: '', owner: '', proposed: milestone.kind === 'retention' && Boolean(proposedHandover) });
  });

  return {
    sections,
    markers,
    dependencies,
    range,
    lookaheadItems,
    critical,
    forecastEnd,
    counts: { proposals: proposalByKey.size, stagesToPlan, tasksToPlan, earlier: earlier.length },
  };
}

/** "‹ 3 earlier" at the start of a milestones line (in the lane); its tooltip lists them (newest first). */
function EarlierChip({ list, violet = false }) {
  if (!list.length) return null;
  const names = [...list].reverse().map((milestone) => `${milestone.label} · ${shortDay(milestone.date)}`);
  return (
    <span
      className={cn(
        'inline-flex h-[18px] items-center gap-0.5 rounded-[5px] border border-dashed bg-surface-2 pl-0.5 pr-1.5 text-[11px] font-semibold',
        violet ? 'tone-violet border-[color:var(--tone)] text-[color:var(--tone-ink)]' : 'border-line text-ink-3'
      )}
      title={`Earlier: ${names.join('; ')}`}
      data-testid={violet ? 'timeline-milestones-earlier-vo' : 'timeline-milestones-earlier'}
    >
      <ChevronLeft className="h-3 w-3" aria-hidden="true" />
      {list.length} earlier
      <span className="sr-only">: {names.join('; ')}</span>
    </span>
  );
}

/**
 * The milestones lane (10 #4): one row with two lines — invoices (+ retention) above, VO approvals below — so a VO
 * approved days after an invoice never hides the invoice's label, and a "‹ N earlier" chip at the start of each line
 * (Gantt row.lines / row.leadChips, as the approved mockup draws it). One line when there is only one kind.
 */
export function billingRows({ billing, shown = [], earlier = [], rangeStart }) {
  const isVo = (milestone) => milestone.kind === 'variation';
  const counts = [
    `${billing.invoiceCount} invoice${billing.invoiceCount === 1 ? '' : 's'}`,
    `${billing.voCount} VO${billing.voCount === 1 ? '' : 's'}`,
    billing.hasRetention ? 'retention' : '',
    earlier.length ? `${earlier.length} earlier` : '',
  ].filter(Boolean).join(' · ');
  const invoiceShown = shown.filter((milestone) => !isVo(milestone));
  const voShown = shown.filter(isVo);
  if (!shown.length) {
    const noteText = earlier.length ? `${earlier.length} earlier — before ${formatDate(rangeStart)}` : 'No invoices or approvals with dates yet';
    return [{
      id: 'billing',
      label: 'Billing & approvals',
      sublabel: counts,
      noteText,
      note: <RowNote>{noteText}</RowNote>,
      height: 44,
      testId: 'timeline-milestones',
    }];
  }
  const earlierInvoices = earlier.filter((milestone) => !isVo(milestone));
  const earlierVos = earlier.filter(isVo);
  if ((!invoiceShown.length && !earlierInvoices.length) || (!voShown.length && !earlierVos.length)) {
    // one kind only: one centred line
    const onlyVos = !invoiceShown.length && !earlierInvoices.length;
    return [{
      id: 'billing',
      label: 'Billing & approvals',
      sublabel: counts,
      milestones: shown,
      leadChips: [<EarlierChip key="earlier" list={earlier} violet={onlyVos} />],
      height: 44,
      testId: 'timeline-milestones',
    }];
  }
  return [{
    id: 'billing',
    label: 'Billing & approvals',
    sublabel: counts,
    lines: 2,
    milestones: shown.map((milestone) => ({ ...milestone, line: isVo(milestone) ? 1 : 0 })),
    leadChips: [
      <EarlierChip key="earlier-invoices" list={earlierInvoices} />,
      <EarlierChip key="earlier-vos" list={earlierVos} violet />,
    ],
    height: 54,
    testId: 'timeline-milestones',
  }];
}

/** Diamonds of one kind on the same day become one ("VO-001…VO-004", "2 invoices"), so labels stay readable. */
export function mergeSameDay(milestones = []) {
  const groups = new Map();
  milestones.forEach((milestone) => {
    const key = `${milestone.kind}|${milestone.date}|${milestone.tone}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(milestone);
  });
  return [...groups.values()].map((list) => {
    if (list.length === 1) return list[0];
    list.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));
    const head = list[0];
    const label = head.kind === 'variation'
      ? `${list[0].label}…${list[list.length - 1].label}`
      : `${list.length} invoices`;
    return { ...head, id: `${head.id}+${list.length - 1}`, label, mono: head.kind === 'variation' };
  }).sort((a, b) => a.date.localeCompare(b.date));
}

/** Rough width of a chip / bar label in px (11.5 px bold text), for the room after the last date. */
const labelPx = (label) => String(label || '').length * 6.8 + 18;

/**
 * The axis end: room after the last bar or marker for what is written after it — a bar's date label
 * ("3–14 Dec") and the chips of the markers near the end ("Proposed handover · 14 Dec"), which are laid out side
 * by side and pushed left at the edge (otherwise "Proposed hando…" / "3–14 De" are clipped). In days of the zoom.
 */
export function paddedEnd(model, zoom = 'month') {
  const ppd = PX_PER_DAY[zoom] || PX_PER_DAY.month;
  const end = toDay(model.range.end);
  const last = toDay(model.range.contentEnd || model.range.end);
  if (end === null || last === null) return model.range.end;
  const near = (model.markers || []).filter((marker) => {
    const day = toDay(marker.date);
    return day !== null && (last - day) * ppd < 400;
  });
  let room = 150; // a bar's date label written after it ("3–14 Dec", "+5 d · forecast 12 Nov")
  if (near.length) {
    const sorted = [...near].sort((a, b) => toDay(a.date) - toDay(b.date));
    const chips = sorted.reduce((sum, marker) => sum + labelPx(marker.label) + 4, 0);
    const lastChip = labelPx(sorted[sorted.length - 1].label);
    const spread = (toDay(sorted[sorted.length - 1].date) - toDay(sorted[0].date)) * ppd;
    // the last chip is centred on its date (half of it after it); the others stand to its left where there is
    // room between their dates, and push it right where there is not
    room = Math.max(room, lastChip / 2 + Math.max(0, chips - lastChip - spread) + 24);
  }
  return dayToIso(Math.max(end, last + Math.ceil(room / ppd)));
}

/** Rows with `children` get a chevron (collapsed by default); open, their children follow them. */
export function expandRows(sections, openIds, onToggle) {
  return sections.map((section) => ({
    ...section,
    rows: section.rows.flatMap((row) => {
      if (!row.children?.length) return [row];
      const open = openIds.has(row.id);
      const toggle = (
        <button
          type="button"
          onClick={() => onToggle(row.id)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the tasks: ${row.label}`}
          className="-ml-1.5 grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`${row.testId || row.id}-toggle`}
        >
          <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} aria-hidden="true" />
        </button>
      );
      return [{ ...row, dot: undefined, marker: toggle }, ...(open ? row.children : [])];
    }),
  }));
}

export function TimelineGantt({ model, zoom = 'month', today, compact = false, showProposalNote = false }) {
  const [openRows, setOpenRows] = useState(() => new Set());
  const sections = useMemo(
    () => expandRows(model.sections, openRows, (rowId) => setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    })),
    [model.sections, openRows]
  );
  return (
    <div className="space-y-2" data-testid="timeline-view" data-zoom={zoom}>
      <Gantt
        zoom={zoom}
        today={today}
        start={model.range.start}
        end={paddedEnd(model, zoom)}
        labelHeader="Stage & task"
        labelWidth={compact ? 150 : 260}
        ariaLabel="Project timeline"
        markers={model.markers}
        sections={sections}
        dependencies={model.dependencies}
        testId="timeline-gantt"
      />
      <GanttLegend
        items={[
          ...(model.counts.proposals ? [{ kind: 'proposed', label: 'Proposed by Auto-plan' }] : []),
          { kind: 'plan', label: 'Scheduled' },
          { kind: 'actual', label: 'Done' },
          { kind: 'late', label: 'Late / slip' },
          { kind: 'past', label: 'Past planned handover' },
          { kind: 'today', label: 'Today' },
          { kind: 'milestone', label: 'Invoice' },
          { kind: 'variation', label: 'VO approved' },
          ...(model.dependencies.length ? [{ kind: 'critical', label: 'Critical path' }] : []),
        ]}
      >
        {showProposalNote ? <>Nothing is saved until you press <b className="font-semibold text-ink-2">Apply plan</b></> : null}
      </GanttLegend>
    </div>
  );
}

/** #7: the next three weeks (from today) as a print-ready page; the browser's print dialog saves the PDF. */
export function openLookahead({ project, items, today, generatedBy }) {
  const { rows, from, to } = lookaheadRows(items, { from: today, days: 21 });
  const popup = window.open('', '_blank', 'width=1100,height=900');
  if (!popup) {
    notify.error('Allow pop-ups to export the lookahead');
    return { rows, opened: false };
  }
  popup.document.open();
  popup.document.write(lookaheadHtml({ project, rows, from, to, generatedBy }));
  popup.document.close();
  if (!rows.length) notify.info('Nothing is planned in the next three weeks — the page says so.');
  return { rows, opened: true };
}

export const formatPlanCount = (count) => formatNumber(count, { decimals: 0 });

export function PlanCalendarHint({ appliedPlan }) {
  if (!appliedPlan?.applied_at) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-ink-3">
      <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
      Plan applied {formatDate(appliedPlan.applied_at)}
    </span>
  );
}
